import React, { useEffect, useMemo, useRef, useState } from 'react';
import useCiudadesCatalog from '../../hooks/useCiudadesCatalog';
import useProfesionesCatalog from '../../hooks/useProfesionesCatalog';
import useOccupationFieldsCatalog from '../../hooks/useOccupationFieldsCatalog';
import {
  FLOW_SECTIONS,
  FIELD_CONFIG,
  FLOW_TOTAL_STEPS,
  buildSectionPayload,
  createEmptyProfileForm,
  getSectionFields,
  ADDRESS_FIELD_NAMES,
  OPTIONAL_ADDRESS_FIELDS,
  formatAddressPreview,
} from '../../constants/profileForm';

const ALLOWED_NUMBER_KEYS = new Set([
  'Backspace',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Delete',
  'Home',
  'End',
  'Enter',
  'Escape',
]);

const sanitizeDigits = (value) => value.replace(/\D/g, '');
const sanitizePhoneValue = (value) => {
  if (!value) {
    return '';
  }
  const trimmed = value.replace(/\s+/g, '');
  if (trimmed.startsWith('+')) {
    const digits = sanitizeDigits(trimmed.slice(1));
    return digits.length > 0 ? `+${digits}` : '+';
  }
  return sanitizeDigits(trimmed);
};

const INTEGER_ONLY_MESSAGE = 'Solo se permiten números enteros.';
const REQUIRED_FIELD_MESSAGE = 'Completa este campo antes de continuar.';
const PHONE_FIELD_MESSAGE = 'Ingresa un número telefónico válido (ej. +573001112233).';
const EMAIL_FIELD_MESSAGE = 'Ingresa un correo electrónico válido.';
const LETTER_FIELD_MESSAGE = 'Ingresa solo letras sin números ni símbolos.';

const PHONE_REGEX = /^\+?\d{7,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DIGIT_ONLY_FIELDS = new Set([
  'numeroDocumento',
  'direccionViaNumero',
  'direccionNumeroSecundario',
  'direccionNumeroPlaca',
]);
const PHONE_FIELDS = new Set(['celular', 'telefonoFijo']);
const EMAIL_FIELDS = new Set(['correoElectronico']);
const CITY_FIELDS = new Set(['ciudadExpedicionId', 'ciudadNacimientoId', 'ciudadResidenciaId']);
const OPTIONAL_FIELDS = new Set([
  'ciudadResidenciaId',
  'profesionDetalle',
  'direccionResidencia',
  ...OPTIONAL_ADDRESS_FIELDS,
]);
const LETTER_ONLY_FIELDS = new Set(['direccionViaLetra', 'direccionLetraSecundaria']);
const PROFESSION_REQUIRED_LEVELS = new Set([
  'TECNOLOGO',
  'PREGRADO',
  'ESPECIALIZACION',
  'MAESTRIA',
  'DOCTORADO',
  'POSTDOCTORADO',
]);
const OCCUPATION_REQUIRES_FIELD = new Set([
  'EMPLEADO',
  'INDEPENDIENTE',
  'EMPRESARIO',
  'OTRO',
]);
const isTruthy = (value) => value === true || value === 'true';
const isFalse = (value) => value === false || value === 'false';
const FIELD_MAP = FIELD_CONFIG.reduce((acc, field) => {
  acc[field.name] = field;
  return acc;
}, {});

const shouldDisplayField = (fieldName, values) => {
  const paisExpedicion = values?.paisExpedicion ?? '';
  const nacidoEnColombia = isTruthy(values?.nacidoEnColombia);
  const tieneOtraNacionalidad = isTruthy(values?.otraNacionalidad);
  const viveEnColombiaValor = values?.viveEnColombia ?? '';
  const viveEnColombia = isTruthy(viveEnColombiaValor);
  const noViveEnColombia = isFalse(viveEnColombiaValor);
  const mostrarDireccion = viveEnColombia || values?.paisResidencia === 'CO';
  const nivelEstudios = values?.nivelEstudios ?? '';
  const mostrarProfesion = PROFESSION_REQUIRED_LEVELS.has(nivelEstudios);
  const ocupacion = values?.ocupacion ?? '';
  const mostrarCampoOcupacion = OCCUPATION_REQUIRES_FIELD.has(ocupacion);

  switch (fieldName) {
    case 'departamentoExpedicion':
    case 'ciudadExpedicionId':
      return paisExpedicion === 'CO';
    case 'ciudadExpedicionOtra':
      return Boolean(paisExpedicion) && paisExpedicion !== 'CO';
    case 'paisNacimiento':
      return !nacidoEnColombia;
    case 'departamentoNacimiento':
    case 'ciudadNacimientoId':
      return nacidoEnColombia;
    case 'paisOtraNacionalidad':
      return tieneOtraNacionalidad;
    case 'paisResidencia':
      return noViveEnColombia;
    case 'ciudadResidenciaId':
      return mostrarDireccion;
    case 'profesionId':
    case 'profesionDetalle':
      return mostrarProfesion;
    case 'campoOcupacionId':
      return mostrarCampoOcupacion;
    case 'direccionResidencia':
    case 'direccionViaTipo':
    case 'direccionViaNumero':
    case 'direccionViaLetra':
    case 'direccionBis':
    case 'direccionCuadrante':
    case 'direccionNumeroSecundario':
    case 'direccionLetraSecundaria':
    case 'direccionNumeroPlaca':
    case 'direccionComplemento':
      return mostrarDireccion;
    default:
      return true;
  }
};

const applyDependentDefaults = (values) => {
  const next = { ...values };
  const paisExpedicion = next.paisExpedicion ?? '';
  if (paisExpedicion === 'CO') {
    next.ciudadExpedicionOtra = '';
  } else if (paisExpedicion) {
    next.departamentoExpedicion = '';
    next.ciudadExpedicionId = '';
  } else {
    next.departamentoExpedicion = '';
    next.ciudadExpedicionId = '';
    next.ciudadExpedicionOtra = '';
  }

  if (isTruthy(next.nacidoEnColombia)) {
    next.paisNacimiento = 'CO';
  } else if (next.nacidoEnColombia === 'false') {
    if (next.paisNacimiento === 'CO') {
      next.paisNacimiento = '';
    }
    next.departamentoNacimiento = '';
    next.ciudadNacimientoId = '';
  }

  if (next.paisResidencia && next.paisResidencia !== 'CO') {
    next.ciudadResidenciaId = '';
  }

  if (isFalse(next.otraNacionalidad)) {
    next.paisOtraNacionalidad = '';
  }

  const nivelEstudios = next.nivelEstudios ?? '';
  if (!PROFESSION_REQUIRED_LEVELS.has(nivelEstudios)) {
    next.profesionId = '';
    next.profesionDetalle = '';
  }

  const ocupacion = next.ocupacion ?? '';
  if (!OCCUPATION_REQUIRES_FIELD.has(ocupacion)) {
    next.campoOcupacionId = '';
  }

  if (isTruthy(next.viveEnColombia)) {
    next.paisResidencia = 'CO';
  } else if (isFalse(next.viveEnColombia)) {
    if (next.paisResidencia === 'CO') {
      next.paisResidencia = '';
    }
    ADDRESS_FIELD_NAMES.forEach((field) => {
      next[field] = '';
    });
    next.direccionResidencia = '';
  }

  next.direccionResidencia = formatAddressPreview(next);

  return next;
};
const FLOW_FIELD_MAP = FLOW_SECTIONS.reduce((acc, section) => {
  section.fields.forEach((field) => {
    acc[field.name] = field;
  });
  return acc;
}, {});

const getNormalizedValue = (field, value) => {
  if (value === undefined || value === null) {
    return '';
  }
  const stringValue = typeof value === 'string' ? value : `${value}`;

  if (PHONE_FIELDS.has(field.name)) {
    return sanitizePhoneValue(stringValue);
  }

  if (EMAIL_FIELDS.has(field.name)) {
    return stringValue.trim();
  }

  if (field.type === 'number') {
    const digitsOnly = sanitizeDigits(stringValue);
    if (digitsOnly.length === 0) {
      return '';
    }
    return `${Number.parseInt(digitsOnly, 10)}`;
  }

  if (DIGIT_ONLY_FIELDS.has(field.name)) {
    return sanitizeDigits(stringValue);
  }

  if (LETTER_ONLY_FIELDS.has(field.name)) {
    return stringValue.replace(/[^A-Za-z]/g, '').toUpperCase();
  }

  return stringValue;
};

const getValidationError = (field, value, values = {}) => {
  const normalizedRaw = value ?? '';
  const normalized =
    typeof normalizedRaw === 'string' ? normalizedRaw : `${normalizedRaw}`;
  const isOptionalField = OPTIONAL_FIELDS.has(field.name);

  if (field.name === 'profesionId') {
    const nivel = values?.nivelEstudios ?? '';
    if (PROFESSION_REQUIRED_LEVELS.has(nivel)) {
      return normalized ? null : REQUIRED_FIELD_MESSAGE;
    }
    return null;
  }

  if (field.name === 'profesionDetalle') {
    if (!normalized) {
      return null;
    }
    if (/[<>]/.test(normalized)) {
      return 'No uses los caracteres "<" o ">".';
    }
    return null;
  }

  if (field.name === 'campoOcupacionId') {
    const ocupacion = values?.ocupacion ?? '';
    if (!ocupacion || !OCCUPATION_REQUIRES_FIELD.has(ocupacion)) {
      return null;
    }
    return normalized ? null : REQUIRED_FIELD_MESSAGE;
  }

  if (PHONE_FIELDS.has(field.name)) {
    if (!normalized) {
      return REQUIRED_FIELD_MESSAGE;
    }
    if (!PHONE_REGEX.test(normalized)) {
      return PHONE_FIELD_MESSAGE;
    }
    return null;
  }

  if (EMAIL_FIELDS.has(field.name)) {
    if (!normalized) {
      return OPTIONAL_FIELDS.has(field.name) ? null : REQUIRED_FIELD_MESSAGE;
    }
    if (!EMAIL_REGEX.test(normalized)) {
      return EMAIL_FIELD_MESSAGE;
    }
    return null;
  }

  if (field.type === 'number' || DIGIT_ONLY_FIELDS.has(field.name)) {
    if (!normalized) {
      return isOptionalField ? null : REQUIRED_FIELD_MESSAGE;
    }
    if (!/^\d+$/.test(normalized)) {
      return INTEGER_ONLY_MESSAGE;
    }
    return null;
  }

  if (LETTER_ONLY_FIELDS.has(field.name)) {
    if (!normalized) {
      return isOptionalField ? null : REQUIRED_FIELD_MESSAGE;
    }
    if (!/^[A-Za-z]+$/.test(normalized)) {
      return LETTER_FIELD_MESSAGE;
    }
    return null;
  }

  if (field.type === 'boolean') {
    if (normalized === 'true' || normalized === 'false') {
      return null;
    }
    return isOptionalField ? null : REQUIRED_FIELD_MESSAGE;
  }

  if (field.type === 'date') {
    return normalized ? null : REQUIRED_FIELD_MESSAGE;
  }

  if (typeof normalized === 'string') {
    if (normalized.trim().length === 0) {
      return OPTIONAL_FIELDS.has(field.name) ? null : REQUIRED_FIELD_MESSAGE;
    }
    return null;
  }

  if (normalized) {
    return null;
  }
  return OPTIONAL_FIELDS.has(field.name) ? null : REQUIRED_FIELD_MESSAGE;
};

const normalizeFormValues = (values) => {
  const normalized = { ...values };
  Object.keys(normalized).forEach((key) => {
    const field = FLOW_FIELD_MAP[key];
    if (!field) {
      return;
    }
    normalized[key] = getNormalizedValue(field, normalized[key]);
  });
  normalized.direccionResidencia = formatAddressPreview(normalized);
  return normalized;
};

const ProfileCompletionFlow = ({
  open,
  initialValues,
  completedSections = [],
  onSaveSection,
  onComplete,
  onSkip,
  onClose,
}) => {
  const [formValues, setFormValues] = useState(() => createEmptyProfileForm());
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const {
    catalog: ciudadCatalog,
    getCitiesForDepartment,
    loading: ciudadOptionsLoading,
    error: ciudadOptionsError,
  } = useCiudadesCatalog();
  const {
    options: professionOptions,
    loading: professionOptionsLoading,
    error: professionOptionsError,
    refresh: refreshProfessionCatalog,
  } = useProfesionesCatalog();
  const {
    options: occupationFieldOptions,
    loading: occupationFieldLoading,
    error: occupationFieldError,
    refresh: refreshOccupationCatalog,
  } = useOccupationFieldsCatalog();

  const professionCatalogRequestedRef = useRef(false);
  const occupationCatalogRequestedRef = useRef(false);

  const completedSet = useMemo(
    () => new Set(Array.isArray(completedSections) ? completedSections : []),
    [completedSections],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const baseValues = initialValues ?? createEmptyProfileForm();
    const normalizedBase = normalizeFormValues(baseValues);
    setFormValues(applyDependentDefaults(normalizedBase));
    setErrorMessage('');
    setSubmitting(false);
    setFieldErrors({});
    const firstIncompleteIndex = FLOW_SECTIONS.findIndex(
      (section) => !completedSet.has(section.id),
    );
    setStepIndex(
      firstIncompleteIndex >= 0 ? firstIncompleteIndex : Math.max(FLOW_SECTIONS.length - 1, 0),
    );
  }, [open, initialValues, completedSet]);

  const totalSteps = FLOW_TOTAL_STEPS || 1;
  const currentSection = FLOW_SECTIONS[stepIndex] ?? FLOW_SECTIONS[0];
  const fieldsForSection = useMemo(
    () => getSectionFields(currentSection?.id, { flowOnly: true }),
    [currentSection?.id],
  );
  const visibleFieldsForSection = useMemo(
    () =>
      fieldsForSection
        .filter((field) => !field.meta?.hideInForm)
        .filter((field) => shouldDisplayField(field.name, formValues)),
    [fieldsForSection, formValues],
  );

  useEffect(() => {
    const nivel = formValues?.nivelEstudios ?? '';
    const requiereProfesion = PROFESSION_REQUIRED_LEVELS.has(nivel);
    if (!requiereProfesion) {
      professionCatalogRequestedRef.current = false;
      return;
    }
    if (professionOptions.length > 0) {
      professionCatalogRequestedRef.current = true;
      return;
    }
    if (professionOptionsLoading || professionCatalogRequestedRef.current) {
      return;
    }
    (async () => {
      try {
        professionCatalogRequestedRef.current = true;
        await refreshProfessionCatalog();
      } catch {
        // evitamos loop automático en caso de error
      }
    })();
  }, [
    formValues?.nivelEstudios,
    professionOptions.length,
    professionOptionsLoading,
    refreshProfessionCatalog,
  ]);

  useEffect(() => {
    const ocupacionActual = formValues?.ocupacion ?? '';
    const requiereCampo = OCCUPATION_REQUIRES_FIELD.has(ocupacionActual);
    if (!requiereCampo) {
      occupationCatalogRequestedRef.current = false;
      return;
    }
    if (occupationFieldOptions.length > 0) {
      occupationCatalogRequestedRef.current = true;
      return;
    }
    if (occupationFieldLoading || occupationCatalogRequestedRef.current) {
      return;
    }
    (async () => {
      try {
        occupationCatalogRequestedRef.current = true;
        await refreshOccupationCatalog();
      } catch {
        // evitamos loop automático
      }
    })();
  }, [
    formValues?.ocupacion,
    occupationFieldOptions.length,
    occupationFieldLoading,
    refreshOccupationCatalog,
  ]);
  const getCityOptionsForField = (fieldName) => {
    switch (fieldName) {
      case 'ciudadExpedicionId':
        return getCitiesForDepartment(formValues.departamentoExpedicion) ?? [];
      case 'ciudadNacimientoId':
        return getCitiesForDepartment(formValues.departamentoNacimiento) ?? [];
      case 'ciudadResidenciaId':
        return ciudadCatalog.allCities ?? [];
      default:
        return ciudadCatalog.allCities ?? [];
    }
  };

  const completedCount = completedSet.size;
  const remainingCount = Math.max(totalSteps - completedCount, 0);

  const updateFieldError = (name, message) => {
    setFieldErrors((prev) => {
      if (message) {
        if (prev[name] === message) {
          return prev;
        }
        return { ...prev, [name]: message };
      }
      if (!prev[name]) {
        return prev;
      }
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleNumberKeyDown = () => (event) => {
    if (event.ctrlKey || event.metaKey) {
      const lower = event.key.toLowerCase();
      if (lower === 'a' || lower === 'c' || lower === 'v' || lower === 'x') {
        return;
      }
    }
    if (ALLOWED_NUMBER_KEYS.has(event.key) || /^\d$/.test(event.key)) {
      return;
    }
    event.preventDefault();
  };

  const handleNumberPaste = () => (event) => {
    const text = event.clipboardData?.getData('text') ?? '';
    if (/^\d*$/.test(text)) {
      return;
    }
    event.preventDefault();
  };

  const renderAddressControl = (fieldName) => {
    const definition = FIELD_MAP[fieldName];
    if (!definition) {
      return null;
    }
    const value = formValues[fieldName] ?? '';
    const hasError = Boolean(fieldErrors[fieldName]);
    const baseClass = hasError
      ? 'w-full rounded-lg border border-red-500 bg-[#ffffff] px-3 py-2 text-sm text-[#002E6D] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200'
      : 'w-full rounded-lg border border-blue-300 bg-[#ffffff] px-3 py-2 text-sm text-[#002E6D] focus:border-[#0054c9] focus:outline-none focus:ring-2 focus:ring-[#80aefc]';
    const commonProps = {
      id: fieldName,
      name: fieldName,
      value,
      onChange: handleValueChange(definition),
      className: baseClass,
      'aria-invalid': hasError || undefined,
    };

    const renderError = () =>
      hasError ? (
        <p className="mt-1 text-xs font-semibold text-red-600">
          {fieldErrors[fieldName]}
        </p>
      ) : null;

    const label = (
      <label htmlFor={fieldName} className="text-xs font-semibold text-[#002E6D]">
        {definition.label}
      </label>
    );

    if (definition.options && Array.isArray(definition.options)) {
      return (
        <div className="flex flex-col gap-1">
          {label}
          <select {...commonProps}>
            <option value="">Selecciona una opción</option>
            {definition.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {renderError()}
        </div>
      );
    }

    if (definition.type === 'boolean') {
      return (
        <div className="flex flex-col gap-1">
          {label}
          <select {...commonProps}>
            <option value="">Selecciona una opción</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
          {renderError()}
        </div>
      );
    }

    const inputProps = definition.inputProps ?? {};

    return (
      <div className="flex flex-col gap-1">
        {label}
        <input
          {...commonProps}
          {...inputProps}
          type={inputProps.type ?? 'text'}
        />
        {renderError()}
      </div>
    );
  };

  const renderAddressGroup = () => {
    const preview = formatAddressPreview(formValues);
    const rows = [
      ['direccionViaTipo', 'direccionViaNumero', 'direccionViaLetra', 'direccionBis', 'direccionCuadrante'],
      ['direccionNumeroSecundario', 'direccionLetraSecundaria', 'direccionNumeroPlaca', 'direccionComplemento'],
    ];

    return (
      <div className="flex flex-col gap-4">
        {rows.map((row, index) => (
          <div key={index} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {row.map((fieldName) => (
              <React.Fragment key={fieldName}>{renderAddressControl(fieldName)}</React.Fragment>
            ))}
          </div>
        ))}
        <div className="rounded-lg border border-blue-300 bg-[#caddff] px-3 py-2 text-sm text-[#002E6D]">
          {preview
            ? preview
            : 'Completa los campos de la dirección para generar el formato estándar.'}
        </div>
      </div>
    );
  };

  const handleValueChange = (field) => (event) => {
    const { name } = field;
    const rawValue = event.target.value;
    setErrorMessage('');

    const normalized = getNormalizedValue(field, rawValue);
    let nextValues = null;
    setFormValues((prev) => {
      const next = { ...prev, [name]: normalized };

      if (name === 'nacidoEnColombia') {
        if (normalized === 'true') {
          next.paisNacimiento = 'CO';
        } else if (normalized === 'false') {
          next.paisNacimiento =
            prev.paisNacimiento && prev.paisNacimiento !== 'CO'
              ? prev.paisNacimiento
              : '';
          next.departamentoNacimiento = '';
          next.ciudadNacimientoId = '';
        } else {
          next.paisNacimiento = '';
          next.departamentoNacimiento = '';
          next.ciudadNacimientoId = '';
        }
      }

      if (name === 'paisNacimiento') {
        if (normalized === 'CO') {
          next.nacidoEnColombia = 'true';
        } else if (normalized) {
          next.nacidoEnColombia = 'false';
        }
      }

      if (name === 'paisExpedicion') {
        if (normalized === 'CO') {
          next.ciudadExpedicionOtra = '';
        } else if (normalized) {
          next.departamentoExpedicion = '';
          next.ciudadExpedicionId = '';
        } else {
          next.departamentoExpedicion = '';
          next.ciudadExpedicionId = '';
          next.ciudadExpedicionOtra = '';
        }
      }

      if (name === 'paisResidencia') {
        if (normalized !== 'CO') {
          next.ciudadResidenciaId = '';
        }
        if (normalized === 'CO') {
          next.viveEnColombia = 'true';
        } else if (normalized) {
          next.viveEnColombia = 'false';
        }
      }

      if (name === 'departamentoExpedicion') {
        next.ciudadExpedicionId = '';
      }

      if (name === 'departamentoNacimiento') {
        next.ciudadNacimientoId = '';
      }

      if (name === 'otraNacionalidad') {
        if (normalized !== 'true') {
          next.paisOtraNacionalidad = '';
        }
      }

      if (name === 'nivelEstudios') {
        if (!PROFESSION_REQUIRED_LEVELS.has(normalized)) {
          next.profesionId = '';
          next.profesionDetalle = '';
        }
        if (
          PROFESSION_REQUIRED_LEVELS.has(normalized) &&
          !professionOptionsLoading &&
          professionOptions.length === 0 &&
          (!professionCatalogRequestedRef.current || professionOptionsError)
        ) {
          if (!professionCatalogRequestedRef.current) {
            professionCatalogRequestedRef.current = true;
          }
          refreshProfessionCatalog().catch(() => {});
        }
      }

      if (name === 'ocupacion') {
        if (!OCCUPATION_REQUIRES_FIELD.has(normalized)) {
          next.campoOcupacionId = '';
        }
        if (
          OCCUPATION_REQUIRES_FIELD.has(normalized) &&
          !occupationFieldLoading &&
          occupationFieldOptions.length === 0 &&
          (!occupationCatalogRequestedRef.current || occupationFieldError)
        ) {
          if (!occupationCatalogRequestedRef.current) {
            occupationCatalogRequestedRef.current = true;
          }
          refreshOccupationCatalog().catch(() => {});
        }
      }

      if (name === 'viveEnColombia') {
        if (normalized === 'true') {
          next.paisResidencia = 'CO';
        } else if (normalized === 'false') {
          if (prev.paisResidencia === 'CO') {
            next.paisResidencia = '';
          }
          ADDRESS_FIELD_NAMES.forEach((fieldName) => {
            next[fieldName] = '';
          });
          next.direccionResidencia = '';
        }
      }

      next.direccionResidencia = formatAddressPreview(next);
      nextValues = next;

      return next;
    });

    const evaluationValues = nextValues ?? formValues;
    const validationError = getValidationError(field, normalized, evaluationValues);
    updateFieldError(name, validationError);

    if (name === 'nacidoEnColombia') {
      updateFieldError('paisNacimiento', null);
      updateFieldError('departamentoNacimiento', null);
      updateFieldError('ciudadNacimientoId', null);
    }

    if (name === 'paisNacimiento') {
      updateFieldError('nacidoEnColombia', null);
      updateFieldError('departamentoNacimiento', null);
      updateFieldError('ciudadNacimientoId', null);
    }

    if (name === 'paisExpedicion') {
      updateFieldError('departamentoExpedicion', null);
      updateFieldError('ciudadExpedicionId', null);
      updateFieldError('ciudadExpedicionOtra', null);
    }

    if (name === 'departamentoExpedicion') {
      updateFieldError('ciudadExpedicionId', null);
    }

    if (name === 'departamentoNacimiento') {
      updateFieldError('ciudadNacimientoId', null);
    }

    if (name === 'otraNacionalidad') {
      updateFieldError('paisOtraNacionalidad', null);
    }

    if (name === 'nivelEstudios') {
      const profesionField = FIELD_MAP.profesionId;
      if (profesionField) {
        updateFieldError(
          'profesionId',
          getValidationError(
            profesionField,
            evaluationValues?.profesionId ?? '',
            evaluationValues,
          ),
        );
      }
      const profesionDetalleField = FIELD_MAP.profesionDetalle;
      if (profesionDetalleField) {
        updateFieldError(
          'profesionDetalle',
          getValidationError(
            profesionDetalleField,
            evaluationValues?.profesionDetalle ?? '',
            evaluationValues,
          ),
        );
      }
    }

    if (name === 'ocupacion') {
      const campoField = FIELD_MAP.campoOcupacionId;
      if (campoField) {
        updateFieldError(
          'campoOcupacionId',
          getValidationError(
            campoField,
            evaluationValues?.campoOcupacionId ?? '',
            evaluationValues,
          ),
        );
      }
    }

    if (name === 'viveEnColombia') {
      updateFieldError('paisResidencia', null);
      ADDRESS_FIELD_NAMES.forEach((fieldName) => {
        updateFieldError(fieldName, null);
      });
      updateFieldError('direccionResidencia', null);
    }

    if (name === 'paisResidencia') {
      updateFieldError('viveEnColombia', null);
    }
  };

  const handlePrevious = () => {
    setErrorMessage('');
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const persistSection = async (intent) => {
    if (!currentSection) {
      if (intent === 'next') {
        setStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
      } else if (intent === 'submit') {
        onClose?.();
      }
      return;
    }

    const normalizedUpdates = {};
    let needsNormalization = false;
    let hasErrors = false;
    let hasMissingValues = false;
    const evaluationValues = { ...formValues };

    fieldsForSection.forEach((field) => {
      const currentValue = evaluationValues[field.name];
      const normalized = getNormalizedValue(field, currentValue);
      if (normalized !== currentValue) {
        normalizedUpdates[field.name] = normalized;
        needsNormalization = true;
        evaluationValues[field.name] = normalized;
      }
    });

    const visibleFields = fieldsForSection.filter((field) =>
      shouldDisplayField(field.name, evaluationValues),
    );

    const hiddenFields = fieldsForSection.filter(
      (field) => !shouldDisplayField(field.name, evaluationValues),
    );

    visibleFields.forEach((field) => {
      const value = evaluationValues[field.name];
      const error = getValidationError(field, value, evaluationValues);
      if (error) {
        hasErrors = true;
        if (error === REQUIRED_FIELD_MESSAGE) {
          hasMissingValues = true;
        }
      }
      updateFieldError(field.name, error);
    });

    hiddenFields.forEach((field) => {
      updateFieldError(field.name, null);
    });

    const preparedFormValues = needsNormalization
      ? { ...formValues, ...normalizedUpdates }
      : formValues;
    const adjustedFormValues = applyDependentDefaults(preparedFormValues);

    if (hasErrors) {
      setErrorMessage(
        hasMissingValues
          ? 'Completa todos los campos de esta etapa antes de continuar.'
          : 'Corrige los campos marcados en rojo antes de continuar.',
      );
      return;
    }

    setFormValues(adjustedFormValues);

    if (!onSaveSection) {
      if (intent === 'next') {
        setStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
      } else if (intent === 'submit') {
        onClose?.();
      }
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    try {
      const sectionPayload = buildSectionPayload(adjustedFormValues, currentSection.id, {
        skipEmpty: true,
        flowOnly: true,
      });
      const updatedUsuario = await onSaveSection(currentSection.id, {
        formValues: adjustedFormValues,
        payload: sectionPayload,
      });

      if (intent === 'next') {
        setStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
      } else if (intent === 'submit') {
        if (onComplete) {
          await onComplete(updatedUsuario ?? null);
        }
        onClose?.();
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'No fue posible guardar tu información. Intenta nuevamente.';
      const normalizedMessage = message.toLowerCase();
      const errorFields = fieldsForSection.filter((field) =>
        shouldDisplayField(field.name, formValues),
      );
      errorFields
        .filter((field) => field.type === 'number')
        .forEach((field) => {
          if (
            normalizedMessage.includes(field.name.toLowerCase()) ||
            normalizedMessage.includes(field.label.toLowerCase()) ||
            normalizedMessage.includes('número') ||
            normalizedMessage.includes('numero')
          ) {
            updateFieldError(field.name, INTEGER_ONLY_MESSAGE);
          }
        });
      errorFields
        .filter((field) => DIGIT_ONLY_FIELDS.has(field.name))
        .forEach((field) => {
          if (
            normalizedMessage.includes(field.name.toLowerCase()) ||
            normalizedMessage.includes(field.label.toLowerCase()) ||
            normalizedMessage.includes('número') ||
            normalizedMessage.includes('numero')
          ) {
            updateFieldError(field.name, INTEGER_ONLY_MESSAGE);
          }
        });
      errorFields
        .filter((field) => LETTER_ONLY_FIELDS.has(field.name))
        .forEach((field) => {
          if (
            normalizedMessage.includes(field.name.toLowerCase()) ||
            normalizedMessage.includes(field.label.toLowerCase()) ||
            normalizedMessage.includes('letra')
          ) {
            updateFieldError(field.name, LETTER_FIELD_MESSAGE);
          }
        });
      errorFields
        .filter((field) => PHONE_FIELDS.has(field.name))
        .forEach((field) => {
          if (
            normalizedMessage.includes(field.name.toLowerCase()) ||
            normalizedMessage.includes('teléfono') ||
            normalizedMessage.includes('telefono') ||
            normalizedMessage.includes('celular')
          ) {
            updateFieldError(field.name, PHONE_FIELD_MESSAGE);
          }
        });
      errorFields
        .filter((field) => EMAIL_FIELDS.has(field.name))
        .forEach((field) => {
          if (
            normalizedMessage.includes(field.name.toLowerCase()) ||
            normalizedMessage.includes('correo') ||
            normalizedMessage.includes('email')
          ) {
            updateFieldError(field.name, EMAIL_FIELD_MESSAGE);
          }
        });
      if (
        normalizedMessage.includes('departamento de expedición') ||
        normalizedMessage.includes('departamento de expedicion')
      ) {
        updateFieldError(
          'departamentoExpedicion',
          'Selecciona el departamento de expedición para documentos expedidos en Colombia.',
        );
      }
      if (
        normalizedMessage.includes('ciudad de expedición') ||
        normalizedMessage.includes('ciudad de expedicion')
      ) {
      const ciudadFieldVisible = shouldDisplayField('ciudadExpedicionId', formValues);
      updateFieldError(
          ciudadFieldVisible ? 'ciudadExpedicionId' : 'ciudadExpedicionOtra',
          ciudadFieldVisible
            ? 'Selecciona la ciudad de expedición para documentos expedidos en Colombia.'
            : 'Ingresa la ciudad de expedición del país seleccionado.',
        );
      }
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!onSkip) {
      onClose?.();
      return;
    }
    setSubmitting(true);
    setErrorMessage('');
    try {
      await onSkip();
      setSubmitting(false);
      onClose?.();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'No fue posible diferir este paso. Intenta nuevamente.';
      setErrorMessage(message);
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    if (!shouldDisplayField(field.name, formValues)) {
      return null;
    }
    if (field.meta?.addressGroupRoot) {
      return renderAddressGroup();
    }
    const { type: inputTypeOverride, ...restInputProps } = field.inputProps ?? {};
    const value = formValues[field.name] ?? '';
    const hasError = Boolean(fieldErrors[field.name]);
    const isCityField = CITY_FIELDS.has(field.name);
    let effectiveOptions = [];
    let selectLoading = false;
    let selectError = null;
    if (isCityField) {
      effectiveOptions = getCityOptionsForField(field.name);
      selectLoading = ciudadOptionsLoading;
      selectError = ciudadOptionsError;
    } else if (field.name === 'profesionId') {
      effectiveOptions = professionOptions;
      selectLoading = professionOptionsLoading;
      selectError = professionOptionsError;
    } else if (field.name === 'campoOcupacionId') {
      effectiveOptions = occupationFieldOptions;
      selectLoading = occupationFieldLoading;
      selectError = occupationFieldError;
    } else if (Array.isArray(field.options)) {
      effectiveOptions = field.options;
    }
    const canRetryProfession =
      field.name === 'profesionId' && selectError && !professionOptionsLoading;
    const canRetryOccupation =
      field.name === 'campoOcupacionId' && selectError && !occupationFieldLoading;
    const handleRetry = () => {
      if (canRetryProfession) {
        professionCatalogRequestedRef.current = true;
        refreshProfessionCatalog().catch(() => {});
      } else if (canRetryOccupation) {
        occupationCatalogRequestedRef.current = true;
        refreshOccupationCatalog().catch(() => {});
      }
    };
    const inputBaseClass =
      'w-full rounded-lg border border-blue-200 bg-[#cce0ff] px-3 py-2 text-sm text-[#002E6D] focus:border-[#0054c9] focus:outline-none focus:ring-2 focus:ring-[#80aefc]';
    const inputClassName = hasError
      ? `${inputBaseClass} border-red-500 focus:border-red-500 focus:ring-red-200`
      : inputBaseClass;
    const commonProps = {
      id: field.name,
      name: field.name,
      value,
      onChange: handleValueChange(field),
      className: inputClassName,
      disabled: submitting,
      'aria-invalid': hasError || undefined,
    };
    const combinedProps = { ...commonProps, ...restInputProps };

    if (DIGIT_ONLY_FIELDS.has(field.name) && field.type !== 'number') {
      combinedProps.inputMode = combinedProps.inputMode ?? 'numeric';
      combinedProps.pattern = combinedProps.pattern ?? '\\d*';
    }

    if (PHONE_FIELDS.has(field.name)) {
      combinedProps.inputMode = combinedProps.inputMode ?? 'tel';
      combinedProps.pattern = combinedProps.pattern ?? '\\+?\\d*';
      combinedProps.placeholder =
        combinedProps.placeholder ?? 'Ej: +573001112233';
    }

    if (isCityField || field.meta?.catalog || (field.options && Array.isArray(field.options))) {
      const selectDisabled =
        Boolean(combinedProps.disabled) || selectLoading || submitting;
      const selectProps = { ...combinedProps, disabled: selectDisabled };
      const placeholderLabel = selectLoading
        ? 'Cargando opciones...'
        : 'Selecciona una opción';
      return (
        <>
          <select {...selectProps}>
            <option value="">{placeholderLabel}</option>
            {effectiveOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {hasError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
          ) : selectError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">
              {selectError.message ||
                'No fue posible cargar las opciones. Intenta nuevamente.'}
              {(canRetryProfession || canRetryOccupation) && (
                <button
                  type="button"
                  className="ml-2 underline"
                  onClick={handleRetry}
                >
                  Reintentar
                </button>
              )}
            </p>
          ) : null}
        </>
      );
    }

    if (field.type === 'boolean') {
      return (
        <>
          <select {...combinedProps}>
            <option value="">Selecciona una opción</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
          {hasError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
          ) : null}
        </>
      );
    }

    const inputType =
      inputTypeOverride ??
      (field.type === 'number'
        ? 'number'
        : field.type === 'date'
        ? 'date'
        : 'text');

    if (field.type === 'number') {
      const { inputMode, pattern, onKeyDown, onPaste, ...restProps } = combinedProps;
      const enforceKeyDown = handleNumberKeyDown();
      const enforcePaste = handleNumberPaste();
      const numberProps = {
        ...restProps,
        onChange: handleValueChange(field),
        inputMode: inputMode ?? 'numeric',
        pattern: pattern ?? '[0-9]*',
        onKeyDown: (event) => {
          enforceKeyDown(event);
          if (typeof onKeyDown === 'function') {
            onKeyDown(event);
          }
        },
        onPaste: (event) => {
          enforcePaste(event);
          if (typeof onPaste === 'function') {
            onPaste(event);
          }
        },
      };
      return (
        <>
          <input {...numberProps} type={inputType} />
          {hasError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
          ) : null}
        </>
      );
    }

    return (
      <>
        <input {...combinedProps} type={inputType} />
        {hasError ? (
          <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
        ) : null}
      </>
    );
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 px-4 py-8">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-[#e8f2f4] shadow-2xl">
        <header className="flex items-start justify-between bg-[#0346a4] px-6 py-4 text-white">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide">
              Completa tu perfil
            </p>
            <h2 className="text-2xl font-bold">Guarda tus datos para futuras inversiones</h2>
            <p className="mt-1 text-sm text-blue-100">
              Al completar estas etapas evitamos pedirte los mismos datos cada vez que quieras invertir
              en un CDT o FIC, y cumplimos con la regulación.
            </p>
            <p className="mt-2 text-xs font-semibold text-blue-100">
              Has guardado {completedCount} de {totalSteps} etapas.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {typeof onClose === 'function' ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-white/60 px-2 py-1 text-xs font-semibold text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:opacity-80"
                disabled={submitting}
              >
                Cerrar
              </button>
            ) : null}
            {typeof onSkip === 'function' ? (
              <button
                type="button"
                onClick={handleSkip}
                className="rounded-lg border border-white/60 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:opacity-80"
                disabled={submitting}
              >
                Tal vez después
              </button>
            ) : null}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-[#0346a4]">
              Paso {stepIndex + 1} de {totalSteps}: {currentSection?.label}
            </div>
            <div className="flex gap-2">
              {FLOW_SECTIONS.map((section, index) => {
                const isCompleted = completedSet.has(section.id);
                const isCurrent = index === stepIndex;
                const baseColor = isCompleted ? 'bg-[#0346a4]' : 'bg-[#a9c3f5]';
                const currentColor = isCompleted ? 'bg-[#02367f]' : 'bg-[#4d7bd1]';
                return (
                  <div
                    key={section.id}
                    className={`h-1.5 w-16 rounded-full transition ${
                      isCurrent ? currentColor : baseColor
                    }`}
                  />
                );
              })}
            </div>
          </div>

          {remainingCount > 0 && (
            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs font-semibold text-[#0346a4]">
              Te faltan {remainingCount} etapa{remainingCount === 1 ? '' : 's'} por completar para
              finalizar tu perfil.
            </div>
          )}

          <div className="mt-6 space-y-4">
            {visibleFieldsForSection.length === 0 ? (
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-[#032c65]">
                Esta etapa no requiere información adicional.
              </div>
            ) : (
              visibleFieldsForSection.map((field) => (
                <label key={field.name} className="block">
                  <span className="mb-1 block text-sm font-semibold text-[#032c65]">
                    {field.label}
                  </span>
                  {renderField(field)}
                </label>
              ))
            )}
          </div>

          {errorMessage && (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-100 px-4 py-3 text-sm text-red-800">
              {errorMessage}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between bg-[#e8f2f4] px-6 py-4">
          {typeof onClose === 'function' ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-transparent px-4 py-2 text-sm font-semibold text-[#032c65] transition hover:bg-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0346a4]/60 disabled:cursor-not-allowed disabled:opacity-80"
              disabled={submitting}
            >
              Cerrar
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handlePrevious}
              className="rounded-lg border border-[#0346a4] px-4 py-2 text-sm font-semibold text-[#0346a4] transition hover:bg-[#0346a4] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0346a4]/60 disabled:cursor-not-allowed disabled:border-blue-200 disabled:text-blue-200"
              disabled={submitting || stepIndex === 0}
            >
              Anterior
            </button>
            {stepIndex < totalSteps - 1 ? (
              <button
                type="button"
                onClick={() => persistSection('next')}
                className="rounded-lg bg-[#0346a4] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#02367f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0346a4]/60 disabled:cursor-not-allowed disabled:bg-[#657fb8]"
                disabled={submitting}
              >
                Guardar y continuar
              </button>
            ) : (
              <button
                type="button"
                onClick={() => persistSection('submit')}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-300 disabled:cursor-not-allowed disabled:bg-green-400"
                disabled={submitting}
              >
                Finalizar
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ProfileCompletionFlow;
